import { useEffect } from 'react'
import { errors } from '@retailer-platform/dashboard/errors'
import { RETAILER_APP_ID_MAP } from '@retailer-platform/domain-shared-enterprise-tools'
import { useGetRequestContexts } from '../../api/getRequestContexts.hooks'
import { type InstacartCustomersSharedV1RequestContext } from '../../api'
import { type Context, type RequestContext } from './types'

export interface GetValidRequestContextOptions {
  retailerId: string
}

function mapRequestContextUpstreamModelToDomain(
  context: InstacartCustomersSharedV1RequestContext
): Partial<RequestContext> {
  return {
    clientConfigurationId: context.clientConfigurationId,
    storeConfigurationId: context.storeConfigurationId,
    countryId: context.countryId,
  }
}

function isDomainRequestContext(context: Partial<RequestContext>): context is RequestContext {
  return Boolean(context.clientConfigurationId && context.countryId && context.storeConfigurationId)
}

/**
 * TODO: this is temporary solution to filter out SPR context
 * follow up ticket ENT-53092 https://instacart.atlassian.net/browse/ENT-53092
 */
function filterSprContext(context: RequestContext, retailerId: string): boolean {
  const RETAILER_ID_SPROUTS = '279'

  if (retailerId === RETAILER_ID_SPROUTS) {
    return context.clientConfigurationId === 1
  }

  return true
}

const getValidRequestContexts = (
  requestContexts: InstacartCustomersSharedV1RequestContext[] | undefined,
  retailerId: string
): RequestContext[] => {
  if (!requestContexts) {
    return []
  }

  return requestContexts
    .map(mapRequestContextUpstreamModelToDomain)
    .filter(isDomainRequestContext)
    .filter(context => filterSprContext(context, retailerId))
}

export const useGetValidRequestContext = ({ retailerId }: GetValidRequestContextOptions) => {
  const retailerAppId = RETAILER_APP_ID_MAP.get(retailerId)
  const { requestContexts, requestContextsLoading, requestContextsError } = useGetRequestContexts()
  const validRequestContexts = getValidRequestContexts(requestContexts, retailerId)
  const contextError = !retailerAppId && validRequestContexts && validRequestContexts.length != 1

  useEffect(
    () => {
      if (!requestContextsLoading && contextError) {
        if (
          requestContextsError?.graphQLErrors?.some(
            ({ message }) => message === 'Account was not set for context'
          )
        ) {
          // The user's session has expired and they will be redirected to the login page shortly
        } else {
          // The retailer is misconfigured - log the error so we can action it.
          // The user will be shown an error message instructing them to contact support.
          errors.captureMessage('ordersOperationalDashboardContextError', {
            level: 'error',
            extra: {
              retailerId,
              retailerAppId,
              requestContexts,
              validRequestContexts,
            },
          })
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [requestContextsLoading]
  )

  const context: Context = {
    adminRequestContext: retailerAppId ? { retailerAppId } : undefined,
    requestContext: validRequestContexts?.[0],
  }

  return {
    context,
    validRequestContexts,
    contextError,
    error: requestContextsError,
    loading: requestContextsLoading,
  }
}
